import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, DEFINITION_KINDS, define } from '../types.js';
import { encryptedString } from '../types/encryptedString.js';

/**
 * API Configuration Schema
 *
 * Stores customer/organization API configuration including:
 * - HMAC shared secrets for anonymous JWT handshake authentication
 * - OAuth credentials for HopDrive REST API access
 *
 * HMAC secrets are used to generate hash values that serve as a trusted source
 * for handshaking anonymous JWTs for pages not requiring login. Claims and IDs
 * are extracted from the encrypted HMAC message content and mapped to an
 * anonymous JWT on the HopDrive side.
 *
 * Two HMAC secrets are provided to support key rotation without service interruption.
 */
export const schema = z
  .object({
    /**
     * Primary HMAC shared secret (encrypted).
     * Used to generate hash values for anonymous JWT handshake authentication.
     */
    hmac_shared_secret_1: encryptedString
      .describe('Primary HMAC shared secret for anonymous JWT handshake authentication')
      .optional(),

    /**
     * Secondary HMAC shared secret (encrypted).
     * Used for key rotation - allows switching to a new secret without service interruption.
     */
    hmac_shared_secret_2: encryptedString.describe('Secondary HMAC shared secret for key rotation support').optional(),

    /**
     * OAuth Client ID for HopDrive REST API access.
     */
    oauth_client_id: z.string().describe('OAuth Client ID for HopDrive REST API access').optional(),

    /**
     * OAuth Client Secret for HopDrive REST API access (encrypted).
     */
    oauth_client_secret: encryptedString.describe('OAuth Client Secret for HopDrive REST API access').optional(),
  })
  .strict()
  .describe('API configuration for customer/organization authentication and API access');

export type ApiConfig = z.infer<typeof schema>;

export const definition = define({
  key: 'api_config',
  schema,
  label: 'API Configuration',
  description:
    'Customer/organization API configuration including HMAC shared secrets for anonymous JWT authentication and OAuth credentials for HopDrive REST API access.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.INTEGRATIONS.key,
  warning:
    'Contains sensitive authentication secrets. HMAC secrets and OAuth credentials must be encrypted before storage.',
  writableAtLevels: [LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
});
